import React from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Switch,
  Fab,
  Avatar,
  Grid,
  Divider,
  ButtonBase,
} from '@mui/material';
import {
  Search as SearchIcon,
  Group,
  VerifiedUser,
  PersonRemove,
  PersonAdd,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  BusinessOutlined as BusinessIcon,
  CalendarTodayOutlined as CalendarIcon,
} from '@mui/icons-material';
import { BRAND } from '../../components/auth/authTheme';
import ProtectedImage from '../../components/ProtectedImage';
import { getUserInfoFromToken } from '../../utils/authUtils';

const NAVY = BRAND.navy;
const ORANGE = BRAND.orange;
const BG = '#F8FAFC';

const StatCard = ({ icon, label, value, iconBg, iconColor, onClick, isActive }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      bgcolor: '#fff',
      borderRadius: '12px',
      p: 1.25,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1.5,
      boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
      border: isActive ? `1px solid ${BRAND.orange}` : `1px solid ${BRAND.border}`,
      height: '100%',
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '8px',
        bgcolor: iconBg,
        color: iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: NAVY, lineHeight: 1.1, mb: 0.25 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: BRAND.muted, fontWeight: 500, lineHeight: 1.2 }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

const isEmployerUser = (type) =>
  (type || getUserInfoFromToken().userType || "").toLowerCase() === "employer";

const ACTION_COLORS = {
  view: '#2196f3',
  edit: '#9c27b0',
  delete: '#d32f2f',
};

const CardActionButton = ({ icon, label, color, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
      minHeight: 44,
      py: 1,
      borderRadius: '10px',
      color,
      transition: 'background-color 0.15s',
      '&:active': { bgcolor: `${color}14` },
    }}
  >
    {icon}
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color, lineHeight: 1 }}>
      {label}
    </Typography>
  </ButtonBase>
);

const EmployeeCard = ({ employee, handleView, handleEdit, handleDelete, handleToggleActive, canEdit, canDelete, userType }) => {
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  const initials = `${employee.first_name?.[0] || ""}${employee.last_name?.[0] || ""}`.toUpperCase();
  const role = employee.profile_data?.designation_name || "Employee";
  const company = employee.company?.company_name || "Company";
  const joinedDate = employee.profile_data?.joining_date || "N/A";
  const showOrgAndRole = !isEmployerUser(userType);
  const isActive = Boolean(employee.is_active);
  const faceRegistered = Boolean(employee.face_registered);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '16px',
        mb: 2,
        boxShadow: '0 2px 12px rgba(26, 43, 74, 0.06)',
        border: `1px solid ${BRAND.border}`,
        overflow: 'hidden',
      }}
    >
      {/* Main content */}
      <Box sx={{ display: 'flex', gap: 1.75, p: 2, pb: 1.75 }}>
        {employee.profile_data?.profile_image ? (
          <Box sx={{ width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
            <ProtectedImage
              path={employee.profile_data.profile_image}
              alt={fullName}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ) : (
          <Avatar sx={{ width: 52, height: 52, bgcolor: ORANGE, fontWeight: 600, fontSize: '1rem', flexShrink: 0 }}>
            {initials || "?"}
          </Avatar>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: '1rem',
              lineHeight: 1.3,
              mb: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullName}
          </Typography>

          <Typography
            sx={{
              fontSize: '0.8rem',
              color: ORANGE,
              fontWeight: 600,
              mb: 0.75,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {employee.employee_id}
            {showOrgAndRole && (
              <Typography component="span" sx={{ color: BRAND.muted, fontWeight: 400, fontSize: 'inherit' }}>
                {' · '}{role}
              </Typography>
            )}
          </Typography>

          {showOrgAndRole && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: BRAND.muted, mb: 0.5 }}>
              <BusinessIcon sx={{ fontSize: '0.95rem', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {company}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: BRAND.muted, mb: 0.75 }}>
            <CalendarIcon sx={{ fontSize: '0.95rem', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.78rem' }}>Joined {joinedDate}</Typography>
          </Box>

          {/* Face Registration Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: '20px',
              fontSize: '0.72rem',
              fontWeight: 600,
              bgcolor: faceRegistered ? '#e8f5e9' : '#f5f5f5',
              color: faceRegistered ? '#2e7d32' : '#757575',
              border: `1px solid ${faceRegistered ? '#a5d6a7' : '#e0e0e0'}`,
            }}
          >
            <span style={{ fontSize: '0.85rem' }}>{faceRegistered ? '✅' : '❌'}</span>
            <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'inherit' }}>
              {faceRegistered ? 'Face Registered' : 'Face Pending'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Active status row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: isActive ? 'rgba(22, 163, 74, 0.06)' : 'rgba(107, 114, 128, 0.06)',
          borderTop: `1px solid ${BRAND.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: isActive ? BRAND.success : BRAND.muted,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: NAVY }}>
            {isActive ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={isActive}
          onChange={(e) => handleToggleActive && handleToggleActive(employee, e.target.checked)}
          inputProps={{ 'aria-label': `${isActive ? 'Deactivate' : 'Activate'} ${fullName}` }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ORANGE },
          }}
        />
      </Box>

      {/* Action row */}
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          px: 0.5,
          py: 0.5,
        }}
      >
        <CardActionButton
          icon={<VisibilityIcon sx={{ fontSize: '1.25rem' }} />}
          label="View"
          color={ACTION_COLORS.view}
          onClick={() => handleView(employee)}
        />
        {canEdit && (
          <>
            <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
            <CardActionButton
              icon={<EditIcon sx={{ fontSize: '1.25rem' }} />}
              label="Edit"
              color={ACTION_COLORS.edit}
              onClick={() => handleEdit(employee)}
            />
          </>
        )}
        {canDelete && (
          <>
            <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
            <CardActionButton
              icon={<DeleteIcon sx={{ fontSize: '1.25rem' }} />}
              label="Delete"
              color={ACTION_COLORS.delete}
              onClick={() => handleDelete(employee)}
            />
          </>
        )}
      </Box>
    </Box>
  );
};


const EmployeeMobileView = ({
  employees,
  employee_data,
  searchTerm,
  setSearchTerm,
  handleView,
  handleEdit,
  handleDelete,
  openModalForCreate,
  canCreate,
  canEdit,
  canDelete,
  userType,
  handleToggleActive,
  handleStatCardClick,
  statusFilter,
}) => {
  // Data is pre-filtered by UserListPage based on statusFilter param in URL
  const filteredList = employees;

  return (
    <Box sx={{ pb: 8, pt: 1, px: 2, bgcolor: BG, minHeight: '100vh' }}>
      
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: NAVY, mb: 2 }}>
        Employees
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={6}>
          <StatCard 
            icon={<Group fontSize="small" />}
            label="Total Employees"
            value={employee_data?.total_employees || 0}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
            onClick={() => handleStatCardClick('all')}
            isActive={statusFilter === 'all'}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard 
            icon={<VerifiedUser fontSize="small" />}
            label="Active Employees"
            value={employee_data?.active_employees || 0}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
            onClick={() => handleStatCardClick('active')}
            isActive={statusFilter === 'active'}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard 
            icon={<PersonRemove fontSize="small" />}
            label="Inactive Employees"
            value={employee_data?.inactive_employees || 0}
            iconBg="rgba(255,152,0,0.15)"
            iconColor="#ff9800"
            onClick={() => handleStatCardClick('inactive')}
            isActive={statusFilter === 'inactive'}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard 
            icon={<PersonAdd fontSize="small" />}
            label="New Joiners"
            value={employee_data?.new_joiners_count || 0}
            iconBg="rgba(156,39,176,0.15)"
            iconColor="#9c27b0"
            onClick={() => handleStatCardClick('new')}
            isActive={statusFilter === 'new'}
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        placeholder="Search employee by name, ID or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            borderRadius: '12px',
            border: `1px solid ${BRAND.border}`,
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: ORANGE, borderWidth: '1px' },
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          )
        }}
      />

      <Box sx={{ mt: 1 }}>
        {filteredList.length > 0 ? (
          filteredList.map((emp) => (
             <EmployeeCard 
               key={emp.id}
               employee={emp}
               handleView={handleView}
               handleEdit={handleEdit}
               handleDelete={handleDelete}
               canEdit={canEdit}
               canDelete={canDelete}
               userType={userType}
               handleToggleActive={handleToggleActive}
             />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
             <Typography sx={{ color: BRAND.muted, fontWeight: 500 }}>
               No employees found
             </Typography>
          </Box>
        )}
      </Box>

      {canCreate && (
        <Fab 
          color="primary" 
          aria-label="add" 
          onClick={openModalForCreate}
          size="small"
          sx={{
            position: 'fixed',
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))', // Above bottom nav
            right: 20,
            bgcolor: ORANGE,
            '&:hover': { bgcolor: BRAND.orangeHover },
            boxShadow: '0 4px 16px rgba(204,85,0,0.4)',
            zIndex: 1002,
          }}
        >
          <AddIcon sx={{ fontSize: '1.25rem' }} />
        </Fab>
      )}
    </Box>
  );
};

export default EmployeeMobileView;
